import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatINR } from '@new-wealth/shared';
import { api } from '../api/client.js';
import { LockerAuthorisedUsers } from '../components/LockerAuthorisedUsers.js';
import { SignedAgreementActions } from '../components/SignedAgreementActions.js';

/**
 * Complete locker profile (owner 2026-08-07) — everything about one locker in
 * one place: the LockerHub-live locker/lease/rent/deposit + per-leg payment
 * status and references, layered with NCD's backing, cheques + clearance, and
 * waivers. Reached from a tenant's name on Locker Tenants.
 */

const card = 'bg-surface border border-border rounded-lg shadow-card p-5 mb-4';
const h2 = 'text-xs font-semibold text-text-label uppercase tracking-wide mb-3';
const pick = (o: any, ...keys: string[]) => { for (const k of keys) if (o?.[k] != null && o[k] !== '') return o[k]; return null; };

/** LockerHub records a leg's payment either as a `legs.<leg>` block or a row in
 *  `payments[]` (purpose = rent/deposit). Read whichever is present. */
function legPayment(lh: any, leg: 'deposit' | 'rent') {
  const legBlock = lh?.legs?.[leg] ?? {};
  const pay = (lh?.payments ?? []).find((p: any) => String(p.purpose ?? p.leg ?? '').toLowerCase() === leg) ?? {};
  const status = pick(legBlock, 'status') ?? pick(pay, 'status', 'offline_status') ?? (legBlock.settled || pay.settled ? 'paid' : null);
  return {
    amount: Number(pick(legBlock, 'amount') ?? pick(pay, 'amount') ?? 0) || null,
    status: status ? String(status) : null,
    reference: pick(pay, 'reference', 'utr', 'intent_no') ?? null,
    method: pick(pay, 'payment_method', 'method') ?? null,
    date: pick(pay, 'paid_at', 'settled_at', 'date') ?? null,
  };
}

const isPaid = (s: string | null) => !!s && /paid|settled|success|complete/i.test(s);
const badge = (ok: boolean) => `text-xs rounded px-1.5 py-0.5 ${ok ? 'bg-[color:var(--success-bg)] text-success' : 'bg-[color:var(--warn-bg)] text-warn'}`;

export function LockerProfilePage() {
  const { applicationId } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['locker-profile', applicationId],
    queryFn: () => api.get<any>(`/api/lockers/profile?application_id=${encodeURIComponent(String(applicationId))}`),
  });

  if (isLoading) return <div className="text-text-muted">Loading locker…</div>;
  if (error || !data) return <div className="text-danger">Couldn’t load this locker.</div>;

  const lh = data.lockerhub;
  // NCD lockers are rent-only (owner 2026-08-22) — no deposit is ever collected,
  // so the deposit leg (and its auto-waiver) is never shown here.
  const rent = legPayment(lh, 'rent');
  const lockerNo = pick(lh, 'locker_no', 'locker_number') ?? pick(lh?.allotment, 'locker_number', 'locker_no') ?? data.pledges[0]?.locker_no ?? '—';
  const size = pick(lh, 'locker_size', 'size') ?? data.pledges[0]?.locker_size ?? '—';
  const branch = pick(lh, 'branch_name', 'branch') ?? '—';
  const status = pick(lh, 'account_status', 'status') ?? '—';
  /**
   * OUR record first, LockerHub's only as a fallback.
   *
   * `data.esign` is LockerHub's A19 status, and the code that reads it was
   * written when they owned the signing. Since #421 the agreement, the document
   * and the Digio account are ALL ours — so their status sits at "not started"
   * for ever, whatever actually happened. Reading it made a signed agreement
   * look unsigned and left the Check button unable to ever say otherwise
   * (owner 2026-09-14: "after esigning im not able to know if an aggrment is
   * successfully esigned").
   *
   * `data.signing` is the record NCD keeps: Draft → AwaitingSignature →
   * CustomerSigned → Signed, plus PendingApproval / Rejected for paper.
   */
  const ourStatus = pick(data.signing, 'status') ?? null;
  const esignStatus = ourStatus ?? pick(data.esign, 'status', 'esign_status') ?? pick(lh, 'esign_status') ?? null;
  const signMethod = pick(data.signing, 'method') ?? null;
  /**
   * ONE link; the server decides what is behind it (our own copy, one fetched
   * from Digio on demand, or LockerHub's PDF for an agreement they signed).
   * This page used to build the link from LockerHub's esign_id, then from
   * whichever record existed — each rule wrong for some kind of locker
   * ("Signed agreement not found" for one signed on our Digio, no link at all
   * for one signed on LockerHub's). See resolveSignedAgreement.
   */
  const signedOnLockerHub = ['signed', 'completed'].includes(String(pick(data.esign, 'status', 'esign_status') ?? '').toLowerCase());
  const isSigned = data.signing ? !!data.signing.is_signed : signedOnLockerHub;

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex gap-3 py-1.5 border-b border-border last:border-0 text-sm">
      <div className="w-40 shrink-0 text-text-muted">{label}</div>
      <div className="flex-1 break-words">{children}</div>
    </div>
  );

  return (
    <div className="w-full max-w-4xl">
      <Link to="/app/locker-tenants" className="text-xs text-text-muted hover:text-primary">← Locker tenants</Link>
      <div className="flex items-center gap-3 mt-1 mb-1 flex-wrap">
        <h1 className="text-xl font-bold tracking-tight m-0">Locker {String(lockerNo)}</h1>
        <span className="text-xs rounded px-2 py-0.5 bg-bg">{String(size)}</span>
        <span className="text-xs rounded px-2 py-0.5 bg-bg">{String(status)}</span>
        {data.agreement_no && (
          <span className="font-mono text-sm font-semibold text-text" title="Agreement number">{data.agreement_no}</span>
        )}
        {/* LockerHub's own id. Kept — it is what every route and every call to
            them is keyed on, so support needs it — but it is no longer the
            thing you read first (owner 2026-09-18). */}
        <span className="font-mono text-xs text-text-muted" title="LockerHub application id">{data.locker_application_id}</span>
      </div>
      {data.customer && (
        <p className="text-sm text-text-muted mb-4">
          {/* A walk-in created straight on LockerHub has a name but no NCD
              record, so there is nothing to link TO — a link to
              /app/customers/null is worse than plain text. */}
          {data.customer.id != null
            ? <Link to={`/app/customers/${data.customer.id}`} className="text-primary hover:underline">{data.customer.full_name}</Link>
            : <span className="text-text">{data.customer.full_name}</span>}
          {data.customer.customer_code ? <> <span className="font-mono">{data.customer.customer_code}</span></> : null}
          {data.customer.phone ? ` · ${data.customer.phone}` : ''}
          {data.customer.id == null ? <span className="text-text-muted"> · not linked to an NCD customer</span> : null}
        </p>
      )}

      {data.lockerhub_error && (
        <div className="text-xs text-warn mb-3 bg-[color:var(--warn-bg)] rounded px-3 py-2">
          Couldn’t reach LockerHub for the live locker + payment details ({String(data.lockerhub_error).slice(0, 100)}). NCD-side records below are still shown.
        </div>
      )}

      {/* Locker + lease */}
      <div className={card}>
        <h2 className={h2}>Locker</h2>
        <Row label="Branch">{String(branch)}</Row>
        <Row label="Locker number">{String(lockerNo)}</Row>
        <Row label="Size">{String(size)}</Row>
        <Row label="Status">{String(status)}</Row>
        {pick(lh, 'lease_start') && <Row label="Lease start">{String(pick(lh, 'lease_start'))}</Row>}
        {pick(lh, 'lease_expires_on', 'lease_end') && <Row label="Lease expires">{String(pick(lh, 'lease_expires_on', 'lease_end'))}</Row>}
        {pick(lh?.allotment, 'allotted_on', 'date') && <Row label="Allotted on">{String(pick(lh.allotment, 'allotted_on', 'date'))}</Row>}
      </div>

      {/* Payments — reflected live from LockerHub */}
      <div className={card}>
        <h2 className={h2}>Payments</h2>
        {[{ leg: 'Rent', p: rent }].map(({ leg, p }) => (
          <div key={leg} className="py-2 border-b border-border last:border-0">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="w-40 shrink-0 text-text-muted">{leg}</span>
              <span className="mono font-medium">{p.amount != null ? formatINR(p.amount) : '—'}</span>
              {p.status && <span className={badge(isPaid(p.status))}>{isPaid(p.status) ? 'Paid' : `${p.status} — pending`}</span>}
              {!p.status && <span className="text-xs text-text-muted">status unknown</span>}
            </div>
            {(p.reference || p.method || p.date) && (
              <div className="text-xs text-text-muted mt-1 ml-40 pl-3">
                {p.method ? `${p.method} · ` : ''}{p.reference ? `ref ${p.reference}` : ''}{p.date ? ` · ${String(p.date).slice(0, 10)}` : ''}
              </div>
            )}
          </div>
        ))}
        {!lh && !data.lockerhub_error && <div className="text-sm text-text-muted">No live payment data.</div>}
      </div>

      {/* NCD backing */}
      {data.pledges.length > 0 && (
        <div className={card}>
          <h2 className={h2}>NCD backing</h2>
          {data.pledges.map((p: any) => (
            <div key={p.id} className="flex flex-wrap items-center gap-x-3 py-1.5 border-b border-border last:border-0 text-sm">
              <Link to={`/app/applications/${p.application_id}`} className="text-primary hover:underline font-mono text-xs">{p.application_no}</Link>
              <span className="mono">{formatINR(p.linked_amount)}</span>
              <span className={`text-xs rounded px-1.5 py-0.5 ${p.status === 'active' ? 'bg-[color:var(--warn-bg)] text-warn' : 'bg-bg text-text-muted'}`}>{p.status}</span>
              {p.released_reason && <span className="text-xs text-text-muted">released: {p.released_reason}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Cheques + clearance */}
      {data.cheques.length > 0 && (
        <div className={card}>
          <h2 className={h2}>Cheques</h2>
          {data.cheques.map((q: any) => (
            <div key={q.id} className="flex flex-wrap items-center gap-x-3 py-1.5 border-b border-border last:border-0 text-sm">
              <span className="font-mono text-xs">{q.cheque_no}{q.bank_name ? ` · ${q.bank_name}` : ''}</span>
              <span className="text-xs text-text-muted">{q.leg}</span>
              <span className="mono">{formatINR(q.amount)}</span>
              <span className={`text-xs rounded px-1.5 py-0.5 ${q.status === 'Cleared' ? 'bg-[color:var(--success-bg)] text-success' : q.status === 'Bounced' ? 'bg-[color:var(--danger-bg)] text-danger' : 'bg-[color:var(--warn-bg)] text-warn'}`}>{q.status}</span>
              {q.status === 'Cleared' && !q.lockerhub_settled_at && <span className="text-xs text-danger">cleared, leg not settled on LockerHub</span>}
              {q.reference && <span className="text-xs text-text-muted">ref {q.reference}</span>}
              {q.cleared_on && <span className="text-xs text-text-muted">cleared {q.cleared_on}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Waivers — rent only. The deposit is auto-waived 100% behind the scenes
          (rent-only policy) and is never surfaced: no deposit, no deposit waiver
          on screen. */}
      {data.fee_waivers.filter((w: any) => String(w.leg).toLowerCase() !== 'deposit').length > 0 && (
        <div className={card}>
          <h2 className={h2}>Fee waivers</h2>
          {data.fee_waivers.filter((w: any) => String(w.leg).toLowerCase() !== 'deposit').map((w: any) => (
            <div key={w.id} className="flex flex-wrap items-center gap-x-3 py-1.5 border-b border-border last:border-0 text-sm">
              <span className="text-xs text-text-muted">{w.leg}</span>
              {w.category === 'premium' && <span className="text-xs rounded px-1.5 py-0.5 bg-[color:var(--success-bg)] text-success">★ Premium</span>}
              <span className="mono">{w.waiver_pct != null ? `${w.waiver_pct}%` : w.waiver_amount != null ? formatINR(w.waiver_amount) : '—'}</span>
              <span className={`text-xs rounded px-1.5 py-0.5 ${w.status === 'Approved' ? 'bg-[color:var(--success-bg)] text-success' : w.status === 'Rejected' ? 'bg-[color:var(--danger-bg)] text-danger' : 'bg-[color:var(--warn-bg)] text-warn'}`}>{w.status}</span>
              {w.reason && <span className="text-xs text-text-muted">{w.reason}</span>}
              {w.status === 'Approved' && !w.lockerhub_applied_at && <span className="text-xs text-danger">approved, not yet in force on LockerHub</span>}
            </div>
          ))}
        </div>
      )}

      {/* Who else holds this locker. Saved since the joint-hirer release and
          shown nowhere until now, so a jointly-held locker read as single-held
          on every screen and staff concluded the second holder had not saved
          (owner 2026-09-14). Every holder signs the agreement, so who they are
          is not a detail. */}
      <div className={card}>
        <h2 className={h2}>Holders</h2>
        <Row label="Hirer 1">
          {data.customer
            ? <>{data.customer.full_name}{data.customer.phone ? <span className="text-text-muted"> · {data.customer.phone}</span> : null}</>
            : <span className="text-text-muted">—</span>}
        </Row>
        {(data.hirers ?? []).map((h: any) => (
          <Row key={h.position} label={`Hirer ${h.position}`}>
            {h.full_name}
            {h.phone
              ? <span className="text-text-muted"> · {h.phone}</span>
              // Phone is how the e-sign link reaches them, so its absence is
              // not cosmetic — the agreement cannot complete without it.
              : <span className="text-danger"> · no phone — cannot be sent for e-sign</span>}
            {h.pan ? <span className="text-text-muted font-mono"> · {h.pan}</span> : null}
          </Row>
        ))}
        {(data.hirers ?? []).length === 0 && (
          <div className="text-xs text-text-muted">Single holder — no joint hirers on this locker.</div>
        )}
      </div>

      {/* Agreement / e-sign */}
      <div className={card}>
        <h2 className={h2}>Agreement &amp; e-sign</h2>
        <Row label="Signing status">
          {esignStatus ? String(esignStatus) : <span className="text-text-muted">not started</span>}
          {signMethod ? <span className="text-text-muted"> · {String(signMethod) === 'esign' ? 'e-Sign' : 'on paper'}</span> : null}
        </Row>
        {isSigned && (
          <Row label="Signed agreement">
            <SignedAgreementActions
              applicationId={String(data.locker_application_id)}
              fileStem={`Locker-${String(lockerNo).replace(/[^\w.-]+/g, '_')}-signed-agreement`}
              className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg disabled:opacity-40" />
          </Row>
        )}
        <div className="mt-3">
          <Link to={`/app/locker-enrollment?application_id=${encodeURIComponent(data.locker_application_id)}`} className="text-xs text-primary hover:underline">
            Open in enrollment (payments, allotment, e-sign actions) →
          </Link>
        </div>
      </div>

      {/* Authorised users — add people the holder authorises to operate this
          locker; each needs the holder's e-signed consent (owner 2026-08-22).
          Post-allotment only: shown once the locker has a number. */}
      <div className={card}>
        {String(lockerNo) !== '—'
          ? <LockerAuthorisedUsers applicationId={String(data.locker_application_id)} customerId={data.customer?.id ?? null} />
          : <>
              <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">Authorised users</div>
              <p className="text-sm text-text-muted m-0">Available once the locker is allotted.</p>
            </>}
      </div>
    </div>
  );
}
